// https://github.com/eKoopmans/html2pdf.js

import axios from 'axios';
import html2pdf from 'html2pdf.js';

class Html2pdf {
    /**
     * Метод инициализиурет модуль
     * @param {object} options - опции:
     * targets - массив из кнопок по которым нужно генерить pdf
     * autoload - автоскачивание файла, по умолчанию включен. Режим превью не рекомендуется к использованию в проде.
     * quality - качество от 1 до 5.
     * orientation - ориентация - portrait / landscape
     * pageBreak - селектор для блока после которого следует разорвать страницу
     */
    init(options) {
        this.options = {};
        this.options.targets = options.targets;
        this.options.autoload = options.autoload !== false;
        this.options.quality = options.quality || 5;
        this.options.orientation = options.orientation || 'portrait';
        this.options.pageBreak = options.pageBreak;

        this._bindEvents();
    }

    /**
     * Метод навешивает обработчики событий на кнопки.
     * @private
     */
    _bindEvents() {
        this.options.targets.forEach((item) => {
            item.addEventListener('click', this._start.bind(this));
        });
    }

    /**
     * Метод подготавливает данные, отправляет запрос и открывает pdf
     * @param {object}  event - объект события по которому произошел клик
     * @private
     */
    async _start(event) {
        this._collectData(event);
        this._startPreloader();
        await this._getHtml();
        this._createContainer();
        await this._openPdf();
        this._stopPreloader();
    }

    /**
     * Метод сохраняет дата-атрибуты из кнопки, и подготавливает форм-дату
     * @param {object}  event - объект события по которому произошел клик
     * @private
     */
    _collectData(event) {
        this.pageOffset = pageYOffset;
        this.currentTarget = event.currentTarget;
        // id, ajax, element, file
        this.dataset = {...this.currentTarget.dataset};

        this.formData = new FormData();
        this.formData.set('id', this.dataset.id);

        for (const key in this.currentTarget.dataset) {
            if (Object.prototype.hasOwnProperty.call(this.currentTarget.dataset, key)) {
                this.formData.append(key, this.currentTarget.dataset[key]);
            }
        }
    }

    /**
     * Метод получает html для pdf, путем отправки запроса либо генерации из статичного дом-элемента
     * @private
     */
    async _getHtml() {
        // Если работаем в режиме получение данных из статичного элемента
        if (this.dataset.element) {
            this.html = document.querySelector(`${this.dataset.element}`).innerHTML;

            return;
        }

        // Если работаем в режиме url получаем данные с бэкенда
        if (this.dataset.ajax) {
            try {
                const response = await axios.post(this.dataset.ajax, this.formData);

                this.html = response.data.data.html;
            } catch (err) {
                console.error(`При получении данных для pdf произошла ошибка - ${err}`);
            }
        }
    }

    /**
     * Метод создает контейнер из полученного html
     * @private
     */
    _createContainer() {
        this.pdfContainer = document.createElement('div');
        this.pdfContainer.innerHTML = this.html;
    }

    /**
     * Метод открывает pdf
     * @return {Promise} - возвращает resolve при готовности pdf
     * @private
     */
    _openPdf() {
        const opt = {
            filename   : this.dataset.file || 'flat-pdf.pdf',
            html2canvas: {
                scale: this.options.quality,
                y    : this.pageOffset
            },
            jsPDF: {
                unit       : 'mm',
                format     : 'a4',
                orientation: this.options.orientation
            },
            pagebreak: {after: this.options.pageBreak},
            margin   : 0
        };

        return new Promise((resolve) => {
            if (this.options.autoload) {
                html2pdf()
                    .set(opt)
                    .from(this.pdfContainer)
                    .save()
                    .then(() => {
                        resolve();
                    });

                return;
            }

            html2pdf()
                .set(opt)
                .from(this.pdfContainer)
                .toPdf()
                .get('pdf')
                .then((pdf) => {
                    window.location = pdf.output('bloburl');
                    resolve();
                });
        });
    }

    /**
     * Метод добавляет таргету класс-модификатор загрузки
     * @private
     */
    _startPreloader() {
        this.currentTarget.classList.add('is-loading');
    }

    /**
     * Метод удаляет у таргета класс-модификатор загрузки
     * @private
     */
    _stopPreloader() {
        this.currentTarget.classList.remove('is-loading');
    }
}

export default Html2pdf;
